<script setup lang="ts">
import { computed, ref } from "vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import { formatDate, formatNumber, printPDFBlob } from "../../../../utils/CommonFunctions";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import MessageBox from "../../../../services/MessageBox";

/**
 * Read-only sales-order detail surface.
 *
 * Mirrors the visual language of {@see SalesQuotationsDetails} (hero halo +
 * tinted Quote Summary card + sticky right sidebar) so cashier-side and
 * staff-side flows feel cohesive. Bound to the RAW POS `sales_orders`
 * payload (the wrapped `data` from `/clients/revenue/sales/sales-orders/record`)
 * because the POS Screen uses the same staff API the back office does.
 *
 * Triggered by a sales_order notification on the POS Screen: the buyer pays
 * → connector dispatches a `sales_order` notification → cashier clicks "View
 * order" in the drawer → POSListView fetches the row → this component is
 * mounted in a modal frame so it overlays the cashier's cart without
 * disrupting the active POS session.
 *
 * The freebie detection + delivery-remarks parser are kept identical to the
 * quotation variant so a buyer's BOGO-claimed item still flags FREE here.
 */

const props = defineProps<{
  data: any | null;
  title?: string | null;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "print"): void;
  /** Fired after the staff `SalesOrdersController::convert` endpoint
   *  materialises a Sales Invoice from this order. Parent (POSListView)
   *  can use this to refresh the notifications inbox / dismiss the
   *  originating notification / etc. The payload is the new invoice
   *  record (with uid, document_no, etc.). */
  (e: "invoice-created", invoice: any): void;
}>();

const o = computed(() => props.data ?? {});
const items = computed<any[]>(() => Array.isArray(o.value.items) ? o.value.items : []);
const customer = computed(() => o.value.customer ?? {});
const deliveryAddress = computed(() => o.value.delivery_address ?? null);
const creditTerm = computed(() => o.value.credit_term ?? null);
const salesRep = computed(() => o.value.sales_rep ?? null);
const createdBy = computed(() => o.value.user ?? null);

/* ───────────── Status mapping ─────────────
 * Same matcher the quotation surface uses — green / amber / red / grey
 * keyed on the status name regardless of which join produced it. */

const statusName = computed<string>(() => (
  o.value.record_status
  ?? o.value.status?.name
  ?? items.value[0]?.status
  ?? "—"
));

type Tone = "success" | "warning" | "danger" | "neutral";

const statusTone = computed<Tone>(() => {
  const s = String(statusName.value).toLowerCase();
  if (s.includes("complete") || s.includes("delivered") || s.includes("approv") || s.includes("paid")) return "success";
  if (s.includes("pend") || s.includes("processing") || s.includes("packing") || s.includes("ready")) return "warning";
  if (s.includes("cancel") || s.includes("reject") || s.includes("void") || s.includes("refund")) return "danger";
  return "neutral";
});

const statusPillClass = computed(() => {
  switch (statusTone.value) {
    case "success": return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
    case "warning": return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
    case "danger":  return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
    default:        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  }
});

const statusIconName = computed(() => {
  switch (statusTone.value) {
    case "success": return "CheckCircle2";
    case "warning": return "Clock3";
    case "danger":  return "XCircle";
    default:        return "Circle";
  }
});

const heroRingClass = computed(() => {
  switch (statusTone.value) {
    case "success": return "bg-green-100 ring-8 ring-green-50 dark:bg-green-900/30 dark:ring-green-900/20";
    case "warning": return "bg-amber-100 ring-8 ring-amber-50 dark:bg-amber-900/30 dark:ring-amber-900/20";
    case "danger":  return "bg-red-100 ring-8 ring-red-50 dark:bg-red-900/30 dark:ring-red-900/20";
    default:        return "bg-gray-100 ring-8 ring-gray-50 dark:bg-gray-800 dark:ring-gray-800/40";
  }
});

const heroIconColor = computed(() => {
  switch (statusTone.value) {
    case "success": return "#15803d";
    case "warning": return "#b45309";
    case "danger":  return "#b91c1c";
    default:        return "#4b5563";
  }
});

const heroTitle = computed(() => {
  switch (statusTone.value) {
    case "success": return "Order Confirmed";
    case "warning": return "Order in Progress";
    case "danger":  return "Order Cancelled";
    default:        return "Sales Order";
  }
});

const heroSubtitleSuffix = computed(() => {
  switch (statusTone.value) {
    case "success": return "is paid and ready to process.";
    case "warning": return "is being prepared.";
    case "danger":  return "was cancelled — no further action needed.";
    default:        return "is on file.";
  }
});

const refDisplay = computed(() => {
  if (!o.value) return "—";
  const raw = o.value.document_no
    ?? `${o.value.prefix ?? ""}${o.value.ref_no ?? ""}${o.value.suffix ?? ""}`
    ?? "—";
  return raw && raw !== "—" ? raw : "—";
});

const copied = ref(false);
async function copyRef() {
  if (!refDisplay.value || refDisplay.value === "—") return;
  try {
    await navigator.clipboard.writeText(refDisplay.value);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1500);
  } catch {
    /* clipboard may be blocked */
  }
}

interface ParsedAddressRemarks {
  label?: string;
  contact_name?: string;
  phone?: string;
  island?: string;
  atoll?: string;
  street?: string;
  postal_code?: string | null;
  instructions?: string | null;
  is_default_billing?: boolean;
}

const addressRemarks = computed<ParsedAddressRemarks | null>(() => {
  const raw = deliveryAddress.value?.remarks;
  if (!raw || typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
});

function asNumber(v: unknown): number {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function money(v: unknown, currency = "MVR"): string {
  return `${currency} ${formatNumber(asNumber(v), 2, 2)}`;
}

function num(v: unknown, decimals = 2): string {
  return formatNumber(asNumber(v), decimals, decimals);
}

function isFreebie(line: any): boolean {
  if (line?.is_giveaway === true || line?.is_giveaway === 1) return true;
  return asNumber(line?.price) === 0 && asNumber(line?.net_total) === 0;
}

const currency = computed<string>(() => o.value.currency ?? "MVR");

/* ───────────── Print ─────────────
 * Calls the canonical staff-side `SalesOrdersController::print` endpoint,
 * which renders the SAME PDF the back office prints (TCPDF, base64-chunked
 * response, see SalesOrdersController.php:657). The base64 string is fed
 * into `printPDFBlob` — it decodes the chunks, materialises a Blob, loads
 * it into the hidden `#pdf-printer` iframe, and invokes `print()` on the
 * frame. Falls back to opening in a new tab when the iframe isn't mounted
 * on the host page. */
/* ───────────── Convert to invoice ─────────────
 * Single-click "Create Invoice" surface for the cashier. Calls the existing
 * staff `SalesOrdersController::convert` action (no new backend endpoint —
 * the same code path the back office uses), which:
 *   1. Loads the order + items by uid.
 *   2. If any line is Batch/Serial-tracked, returns the order with a
 *      `batch_serial` marker so the cashier knows to use the back office to
 *      assign batches (POS modal isn't built for batch picking).
 *   3. Otherwise dispatches to SalesInvoicesController::create which mints
 *      a new SalesInvoices row carrying `sales_orders_id` + each line's
 *      `sales_order_items_id`, runs the promo evaluator + tax recompute,
 *      and returns the invoice record plus a base64 PDF.
 *
 * The button hides itself once `existingInvoice` is true so the cashier
 * can't accidentally mint duplicate invoices on the same order. Detection
 * is based on the `invoice` relation `SalesOrdersController::record` eager-
 * loads when the modal opens; after a successful create we also flip a
 * local flag so the UI updates without a refetch round-trip.
 */
const existingInvoice = computed<any | null>(() => o.value?.invoice ?? null);
const createdInvoiceUid = ref<string | null>(null);
const creating = ref(false);

async function createInvoice() {
  if (creating.value) return;
  const uid = o.value?.uid;
  if (!uid) {
    Toast.show("This order doesn't have a uid to invoice.", "warning");
    return;
  }
  if (existingInvoice.value || createdInvoiceUid.value) {
    Toast.show("This order has already been invoiced.", "warning");
    return;
  }

  const orderRef = (o.value?.document_no ?? o.value?.ref_no ?? "this order") as string;
  const confirmed = await MessageBox.confirm({
    title: "Create Invoice",
    message: `Create a Sales Invoice from ${orderRef}?`,
    confirmText: "Create Invoice",
    cancelText: "Cancel",
    type: "info",
  });
  if (!confirmed) return;

  creating.value = true;
  try {
    // The convert endpoint expects `id` (uid) + `convert` (target doc name).
    // Response shape mirrors what SalesInvoicesController::create returns
    // for any other create call: { success, data: { data: <invoice>, file:
    // <base64 pdf> } } — see SalesQuotationsForm.handleSubmit for the same
    // contract on the quotation side.
    const response: any = await api.post(
      "clients/revenue/sales/sales-orders/convert",
      { id: uid, convert: "Sales Invoice" },
    );

    const payload = response?.data ?? {};
    const newInvoice = payload?.data ?? null;

    if (response?.success && newInvoice?.uid) {
      createdInvoiceUid.value = newInvoice.uid;
      Toast.show(
        "Invoice created — " + (newInvoice.document_no ?? newInvoice.ref_no ?? "OK"),
        "info",
      );

      // Auto-print the PDF the backend already generated, matching the
      // staff form behaviour on save. Wrapped so a print mishap (popup
      // blocker, missing iframe) doesn't void the success path.
      if (payload?.file) {
        try {
          await printPDFBlob(payload.file, "Sales Invoice");
        } catch { /* tolerated */ }
      }

      emit("invoice-created", newInvoice);
      // Modal intentionally stays open after a successful convert so the
      // cashier can use the "Print Invoice" button to re-issue the PDF
      // (if the auto-print hit a popup blocker, missing iframe, or the
      // customer asked for a second copy). The user closes manually via
      // the X / Close affordances when they're done.
    } else {
      // The convert action returns the order back with a `batch_serial`
      // marker (via DataExResponse) when one or more lines is Batch- or
      // Serial-tracked. Surface a clear hand-off message — POS modal can't
      // do batch picking, the back office form has the proper UI for it.
      const msg = String(response?.message ?? "");
      if (msg.toLowerCase().includes("batch") || response?.code === "batch_serial") {
        Toast.show(
          "This order has batch/serial items — please invoice it from the back office.",
          "warning",
        );
      } else {
        Toast.show(msg || "Could not create the invoice.", "warning");
      }
    }
  } catch {
    Toast.show("Could not create the invoice.", "error");
  } finally {
    creating.value = false;
  }
}

/* ───────────── Print invoice ─────────────
 * Re-issue the PDF for the invoice linked to this order. The uid comes from
 * one of two places:
 *   - `existingInvoice.uid` — when the order was loaded with a pre-existing
 *     invoice via SalesOrdersController::record's eager-loaded `invoice`
 *     relation (i.e. the cashier reopens an already-invoiced order).
 *   - `createdInvoiceUid` — when the cashier just clicked Create Invoice
 *     this session and the convert endpoint returned a new row.
 * Either way the staff `sales-invoices/print` endpoint renders the canonical
 * TCPDF the back office prints; we feed it into the same `printPDFBlob`
 * helper the order print uses. */
const invoiceUidForPrint = computed<string | null>(
  () => createdInvoiceUid.value ?? (existingInvoice.value?.uid ?? null),
);

const printingInvoice = ref(false);
async function printInvoice() {
  if (printingInvoice.value) return;
  const uid = invoiceUidForPrint.value;
  if (!uid) {
    Toast.show("No invoice is linked to this order yet.", "warning");
    return;
  }
  printingInvoice.value = true;
  try {
    const response: any = await api.post(
      "clients/revenue/sales/sales-invoices/print",
      { id: uid },
    );
    if (response?.success && response.data) {
      await printPDFBlob(response.data, "Sales Invoice");
    } else {
      Toast.show("Could not generate the invoice PDF.", "warning");
    }
  } catch {
    Toast.show("Could not print the invoice.", "error");
  } finally {
    printingInvoice.value = false;
  }
}

const printing = ref(false);
async function printDocument() {
  if (printing.value) return;
  const uid = o.value?.uid;
  if (!uid) {
    Toast.show("This order doesn't have a uid to print.", "warning");
    return;
  }
  printing.value = true;
  try {
    const response: any = await api.post(
      "clients/revenue/sales/sales-orders/print",
      { id: uid },
    );
    if (response?.success && response.data) {
      await printPDFBlob(response.data, "Sales Order");
      // Emit for parents that want to track print activity (analytics etc.)
      // AFTER the PDF stream is staged so the parent can rely on the side
      // effect having actually occurred.
      emit("print");
    } else {
      Toast.show("Could not generate the PDF.", "warning");
    }
  } catch {
    Toast.show("Could not print the order.", "error");
  } finally {
    printing.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col h-full w-full bg-gray-50 dark:bg-gray-900 print:bg-white">
    <!-- ───────────── Top bar (print:hidden) ───────────── -->
    <header
      class="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 flex items-center justify-between gap-3 print:hidden"
    >
      <button
        type="button"
        class="inline-flex items-center gap-2 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition"
        aria-label="Close"
        @click="emit('close')"
      >
        <IconComponent name="X" :size="18" />
        <span class="hidden sm:inline text-sm font-semibold">Close</span>
      </button>

      <div class="flex items-center gap-2 min-w-0">
        <div class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide hidden md:block">
          {{ title || 'Sales Order' }}
        </div>
        <span
          class="inline-flex items-center gap-1 rounded-full text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 whitespace-nowrap"
          :class="statusPillClass"
        >
          <IconComponent :name="statusIconName" :size="12" />
          {{ statusName }}
        </span>
      </div>

      <button
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-60 disabled:cursor-wait"
        :disabled="printing"
        @click="printDocument"
      >
        <IconComponent :name="printing ? 'Loader2' : 'Printer'" :size="16" :class="printing ? 'animate-spin' : ''" />
        <span class="hidden sm:inline">{{ printing ? 'Preparing…' : 'Print' }}</span>
      </button>
    </header>

    <!-- ───────────── Body ───────────── -->
    <div class="flex-1 overflow-auto">
      <div class="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">

        <!-- Hero -->
        <section class="text-center pt-2">
          <div
            class="mx-auto w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mb-4"
            :class="heroRingClass"
          >
            <IconComponent :name="statusIconName" :size="56" :color="heroIconColor" />
          </div>
          <h1 class="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-gray-50 tracking-tight">
            {{ heroTitle }}
          </h1>
          <p class="text-sm md:text-base text-gray-500 dark:text-gray-400 mt-2 max-w-xl mx-auto leading-relaxed">
            Order
            <button
              type="button"
              class="inline-flex items-center gap-1 align-baseline ml-1 font-bold text-primary-600 dark:text-primary-300 hover:text-primary-700 dark:hover:text-primary-200 hover:underline underline-offset-4 transition"
              :title="copied ? 'Copied!' : 'Click to copy reference'"
              @click="copyRef"
            >
              {{ refDisplay }}
              <IconComponent :name="copied ? 'Check' : 'Copy'" :size="14" />
            </button>
            {{ heroSubtitleSuffix }}
          </p>
          <p v-if="o.date_time || customer.name" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            <template v-if="customer.name">Customer: <strong class="text-gray-700 dark:text-gray-200">{{ customer.name }}</strong></template>
            <template v-if="customer.name && o.date_time"> · </template>
            <template v-if="o.date_time">Created {{ formatDate(o.date_time, "yyyy-mm-dd") }}</template>
          </p>
        </section>

        <!-- Two-column main -->
        <div class="grid lg:grid-cols-[1fr_320px] gap-6">
          <!-- LEFT: Items + Customer + Delivery + Remarks -->
          <section class="space-y-5 min-w-0">

            <!-- Items card -->
            <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <header class="bg-primary-50 dark:bg-primary-500/10 px-5 py-4 flex items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700">
                <h2 class="font-bold text-gray-900 dark:text-gray-50 text-lg flex items-center gap-2">
                  <IconComponent name="ShoppingBag" :size="20" class="text-primary-500" />
                  Order Summary
                </h2>
                <span class="rounded-full border border-primary-300 dark:border-primary-500/40 bg-white dark:bg-gray-800 px-3 py-1 text-xs font-semibold text-primary-700 dark:text-primary-300 whitespace-nowrap">
                  {{ items.length }} {{ items.length === 1 ? 'Item' : 'Items' }}
                </span>
              </header>

              <div class="hidden md:grid grid-cols-[minmax(0,2.4fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,1.2fr)] gap-4 px-5 py-2.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                <span>Item</span>
                <span class="text-center">Quantity</span>
                <span class="text-center">Unit Price</span>
                <span class="text-right">Subtotal</span>
              </div>

              <div class="divide-y divide-gray-100 dark:divide-gray-700">
                <article
                  v-for="(line, idx) in items"
                  :key="line.id ?? idx"
                  class="grid grid-cols-1 md:grid-cols-[minmax(0,2.4fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,1.2fr)] gap-2 md:gap-4 px-5 py-4 md:items-center"
                  :class="isFreebie(line) ? 'bg-green-50/60 dark:bg-green-900/10' : ''"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <div class="w-11 h-11 rounded-md bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0 relative">
                      <IconComponent name="Package" :size="22" class="text-primary-400" />
                      <span
                        v-if="isFreebie(line)"
                        class="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-600 text-white text-[9px] font-extrabold flex items-center justify-center shadow"
                        title="Free promotion item"
                      >★</span>
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <div class="text-sm font-bold text-gray-900 dark:text-gray-50 line-clamp-2">
                          {{ line.item ?? '—' }}
                        </div>
                        <span
                          v-if="isFreebie(line)"
                          class="inline-flex items-center gap-1 rounded-full bg-green-600 text-white text-[10px] font-extrabold px-2 py-0.5 tracking-wide uppercase shadow-sm whitespace-nowrap"
                        >
                          <IconComponent name="Gift" :size="11" />
                          Free
                        </span>
                      </div>
                      <div v-if="line.code" class="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
                        {{ line.code }}
                      </div>
                      <div v-if="line.sales_uom" class="text-xs italic text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {{ line.sales_uom }}
                      </div>
                    </div>
                  </div>

                  <div class="md:text-center text-sm flex md:block items-baseline gap-2">
                    <span class="md:hidden text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Qty</span>
                    <span>
                      <span class="font-semibold text-gray-900 dark:text-gray-50">{{ num(line.quantity, 2) }}</span>
                      <span v-if="line.sales_uom" class="text-gray-500 dark:text-gray-400"> {{ line.sales_uom }}</span>
                    </span>
                  </div>

                  <div class="md:text-center text-sm flex md:block items-baseline gap-2 text-gray-700 dark:text-gray-200">
                    <span class="md:hidden text-[11px] uppercase tracking-wide">Unit</span>
                    <span v-if="isFreebie(line)" class="text-green-700 dark:text-green-300 font-bold">FREE</span>
                    <span v-else>{{ num(line.price, 2) }}</span>
                  </div>

                  <div class="md:text-right text-sm font-bold text-gray-900 dark:text-gray-50 flex md:block items-baseline gap-2 justify-between">
                    <span class="md:hidden text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-normal">Subtotal</span>
                    <span v-if="isFreebie(line)" class="text-green-700 dark:text-green-300">FREE</span>
                    <span v-else>{{ num(line.net_total, 2) }}</span>
                  </div>
                </article>

                <div v-if="items.length === 0" class="px-5 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                  No items on this order.
                </div>
              </div>

              <footer class="bg-primary-50 dark:bg-primary-500/10 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                <!-- Full receipt-style breakdown so a cashier eyeballing the
                     modal can verify the math the buyer agreed to. -->
                <dl class="space-y-1.5 text-sm">
                  <div class="flex justify-between gap-3">
                    <dt class="text-gray-500 dark:text-gray-400">Sub Total</dt>
                    <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ money(o.sub_total, currency) }}</dd>
                  </div>
                  <div v-if="asNumber(o.discount_a) > 0" class="flex justify-between gap-3 text-green-700 dark:text-green-400">
                    <dt>Discount</dt>
                    <dd>− {{ money(o.discount_a, currency) }}</dd>
                  </div>
                  <div class="flex justify-between gap-3">
                    <dt class="text-gray-500 dark:text-gray-400">Value Before Tax</dt>
                    <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ money(o.value_before_tax, currency) }}</dd>
                  </div>
                  <div v-if="asNumber(o.service_charge) > 0" class="flex justify-between gap-3">
                    <dt class="text-gray-500 dark:text-gray-400">Service Charge</dt>
                    <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ money(o.service_charge, currency) }}</dd>
                  </div>
                  <div class="flex justify-between gap-3">
                    <dt class="text-gray-500 dark:text-gray-400">Tax</dt>
                    <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ money(o.tax_amount, currency) }}</dd>
                  </div>
                  <div class="flex items-center justify-between gap-3 pt-3 mt-2 border-t-2 border-gray-200 dark:border-gray-700">
                    <dt class="text-base md:text-lg font-bold text-gray-900 dark:text-gray-50">Net Total</dt>
                    <dd class="text-lg md:text-2xl font-extrabold text-primary-600 dark:text-primary-300 whitespace-nowrap">
                      {{ money(o.net_total, currency) }}
                    </dd>
                  </div>
                </dl>
              </footer>
            </div>

            <!-- Customer card -->
            <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <header class="bg-gray-50 dark:bg-gray-900/60 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 class="font-bold text-gray-900 dark:text-gray-50 text-sm flex items-center gap-2">
                  <IconComponent name="User" :size="16" class="text-primary-500" />
                  Customer
                </h2>
              </header>
              <div class="px-5 py-4 text-sm">
                <div class="text-base font-bold text-gray-900 dark:text-gray-50 truncate">
                  {{ customer.name ?? '—' }}
                </div>
                <div v-if="customer.customer_type" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {{ customer.customer_type }}
                </div>
                <dl class="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-2">
                  <div v-if="customer.email" class="flex items-center gap-2">
                    <IconComponent name="Mail" :size="14" class="text-gray-400 flex-shrink-0" />
                    <dd class="text-gray-700 dark:text-gray-200 truncate">{{ customer.email }}</dd>
                  </div>
                  <div v-if="customer.mobile" class="flex items-center gap-2">
                    <IconComponent name="Phone" :size="14" class="text-gray-400 flex-shrink-0" />
                    <dd class="text-gray-700 dark:text-gray-200">{{ customer.mobile }}</dd>
                  </div>
                  <div v-if="customer.tax_registration_no" class="flex items-center gap-2">
                    <IconComponent name="Receipt" :size="14" class="text-gray-400 flex-shrink-0" />
                    <dd class="text-gray-700 dark:text-gray-200">
                      <span class="text-xs text-gray-500 dark:text-gray-400">TIN</span>
                      {{ customer.tax_registration_no }}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <!-- Delivery card -->
            <div v-if="deliveryAddress" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <header class="bg-gray-50 dark:bg-gray-900/60 px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
                <h2 class="font-bold text-gray-900 dark:text-gray-50 text-sm flex items-center gap-2">
                  <IconComponent name="Truck" :size="16" class="text-primary-500" />
                  Delivery
                </h2>
                <span
                  v-if="o.to_deliver"
                  class="text-[10px] font-bold uppercase tracking-wide bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded-full px-2 py-0.5"
                >
                  To Deliver
                </span>
                <span
                  v-else
                  class="text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-full px-2 py-0.5"
                >
                  Pickup
                </span>
              </header>
              <div class="px-5 py-4 text-sm space-y-1.5">
                <div v-if="addressRemarks?.label" class="text-xs font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-300">
                  {{ addressRemarks.label }}
                </div>
                <div v-if="addressRemarks?.contact_name" class="font-bold text-gray-900 dark:text-gray-50">
                  {{ addressRemarks.contact_name }}
                </div>
                <div v-if="addressRemarks?.phone" class="text-gray-700 dark:text-gray-200">
                  {{ addressRemarks.phone }}
                </div>
                <div class="text-gray-700 dark:text-gray-200">
                  {{ deliveryAddress.full_address || deliveryAddress.address || '—' }}
                </div>
                <div v-if="addressRemarks?.instructions" class="text-xs italic text-gray-500 dark:text-gray-400 mt-1">
                  {{ addressRemarks.instructions }}
                </div>
                <div v-if="o.delivery_date" class="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <IconComponent name="CalendarDays" :size="14" class="text-gray-400" />
                  <span class="text-gray-700 dark:text-gray-200">
                    Deliver by <strong>{{ formatDate(o.delivery_date, "yyyy-mm-dd") }}</strong>
                  </span>
                </div>
              </div>
            </div>

            <!-- Remarks -->
            <div v-if="o.remarks" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <header class="bg-gray-50 dark:bg-gray-900/60 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 class="font-bold text-gray-900 dark:text-gray-50 text-sm flex items-center gap-2">
                  <IconComponent name="StickyNote" :size="16" class="text-primary-500" />
                  Remarks
                </h2>
              </header>
              <p class="px-5 py-4 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">
                {{ o.remarks }}
              </p>
            </div>
          </section>

          <!-- RIGHT: Document info + Document trail + Conversion -->
          <aside class="space-y-3 h-fit lg:sticky lg:top-20 print:relative print:top-0">
            <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <h3 class="font-bold text-gray-900 dark:text-gray-50 text-base flex items-center gap-2 mb-3">
                <IconComponent name="FileText" :size="18" class="text-primary-500" />
                Document Info
              </h3>
              <dl class="space-y-2 text-sm">
                <div class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Ref No</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ o.ref_no ?? '—' }}</dd>
                </div>
                <div v-if="o.date_time" class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Date</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ formatDate(o.date_time, "yyyy-mm-dd") }}</dd>
                </div>
                <div v-if="creditTerm" class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Payment Terms</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium">
                    {{ creditTerm.name }}<span v-if="creditTerm.credit_days != null"> · {{ creditTerm.credit_days }}d</span>
                  </dd>
                </div>
                <div class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Currency</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ currency }}</dd>
                </div>
                <div v-if="o.customer_reference" class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Customer Ref</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium truncate">{{ o.customer_reference }}</dd>
                </div>
              </dl>
            </div>

            <!-- ───────────── Create Invoice (primary action) ─────────────
                 Hidden once the order has a linked invoice — either eager-
                 loaded by SalesOrdersController::record via the `invoice`
                 relation, or set locally after a successful convert call.
                 -->
            <button
              v-if="!existingInvoice && !createdInvoiceUid"
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-emerald-700 active:scale-95 transition shadow-sm print:hidden disabled:opacity-60 disabled:cursor-wait"
              :disabled="creating"
              @click="createInvoice"
            >
              <IconComponent :name="creating ? 'Loader2' : 'ReceiptText'" :size="18" :class="creating ? 'animate-spin' : ''" />
              {{ creating ? 'Creating Invoice…' : 'Create Invoice' }}
            </button>
            <template v-else>
              <div
                class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700 print:hidden"
                :title="existingInvoice?.document_no ? ('Invoice ' + existingInvoice.document_no) : 'Invoice already created'"
              >
                <IconComponent name="CheckCircle2" :size="16" />
                <span>
                  Invoice created<span v-if="existingInvoice?.document_no"> — {{ existingInvoice.document_no }}</span>
                </span>
              </div>
              <!-- Re-print affordance for both the just-created flow (when
                   the auto-print on convert got blocked / the customer asks
                   for another copy) and the revisit flow (cashier reopens an
                   already-invoiced order from a notification). Uses the same
                   staff PDF endpoint the back office prints from. -->
              <button
                type="button"
                class="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-emerald-700 active:scale-95 transition shadow-sm print:hidden disabled:opacity-60 disabled:cursor-wait"
                :disabled="printingInvoice || !invoiceUidForPrint"
                @click="printInvoice"
              >
                <IconComponent :name="printingInvoice ? 'Loader2' : 'Printer'" :size="18" :class="printingInvoice ? 'animate-spin' : ''" />
                {{ printingInvoice ? 'Preparing PDF…' : 'Print Invoice' }}
              </button>
            </template>

            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-md font-semibold hover:bg-primary-600 active:scale-95 transition shadow-sm print:hidden disabled:opacity-60 disabled:cursor-wait"
              :disabled="printing"
              @click="printDocument"
            >
              <IconComponent :name="printing ? 'Loader2' : 'Printer'" :size="18" :class="printing ? 'animate-spin' : ''" />
              {{ printing ? 'Preparing PDF…' : 'Print Order' }}
            </button>

            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-300 transition pt-1 print:hidden"
              @click="emit('close')"
            >
              <IconComponent name="X" :size="16" />
              <span class="underline underline-offset-4">Close</span>
            </button>

            <div v-if="salesRep || createdBy || o.created_at" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <h3 class="font-bold text-gray-900 dark:text-gray-50 text-sm flex items-center gap-2 mb-3">
                <IconComponent name="UserCheck" :size="16" class="text-primary-500" />
                Document Trail
              </h3>
              <dl class="space-y-1.5 text-sm">
                <div v-if="salesRep" class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Sales Rep</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium truncate">{{ salesRep.name }}</dd>
                </div>
                <div v-if="createdBy" class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Created By</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium truncate">{{ createdBy.name }}</dd>
                </div>
                <div v-if="o.created_at" class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Created</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ formatDate(o.created_at, "yyyy-mm-dd HH:MM") }}</dd>
                </div>
                <div v-if="o.updated_at" class="flex justify-between gap-3">
                  <dt class="text-gray-500 dark:text-gray-400">Updated</dt>
                  <dd class="text-gray-900 dark:text-gray-50 font-medium">{{ formatDate(o.updated_at, "yyyy-mm-dd HH:MM") }}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media print {
  :deep(*) {
    color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
